/**
 * CommentEditor - Compact TipTap editor for comments
 *
 * Lightweight rich text editor with inline formatting toolbar.
 * Supports bold, italic, strike, code, link, lists, blockquote.
 */

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Send,
} from 'lucide-react';
import clsx from 'clsx';

interface CommentEditorProps {
  content?: string;
  placeholder?: string;
  onSubmit: (html: string) => void;
  submitting?: boolean;
  autoFocus?: boolean;
  submitIcon?: React.ReactNode;
}

export interface CommentEditorHandle {
  clear: () => void;
  focus: () => void;
  getHTML: () => string;
}

export const CommentEditor = forwardRef<CommentEditorHandle, CommentEditorProps>(
  ({ content = '', placeholder = 'Scrivi un commento...', onSubmit, submitting = false, autoFocus = false, submitIcon }, ref) => {
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
          horizontalRule: false,
          link: false,
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: 'text-accent-violet underline',
          },
        }),
        Placeholder.configure({
          placeholder,
          emptyEditorClass: 'is-comment-empty',
        }),
      ],
      content,
      autofocus: autoFocus,
      editorProps: {
        attributes: {
          class: clsx(
            'prose prose-invert max-w-none focus:outline-none px-3 py-2 text-[12px] min-h-[60px] max-h-[200px] overflow-y-auto',
            'prose-p:text-text-secondary prose-p:leading-relaxed prose-p:my-0.5 prose-p:text-[12px]',
            'prose-a:text-accent-violet prose-a:no-underline hover:prose-a:underline',
            'prose-strong:text-text-primary prose-strong:font-semibold',
            'prose-em:text-text-secondary',
            'prose-code:text-accent-cyan prose-code:bg-surface-tertiary prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[11px]',
            'prose-pre:bg-surface-tertiary prose-pre:rounded-md prose-pre:p-2 prose-pre:text-[11px]',
            'prose-blockquote:border-l-2 prose-blockquote:border-accent-violet prose-blockquote:pl-2 prose-blockquote:italic prose-blockquote:text-text-tertiary prose-blockquote:my-1',
            'prose-ul:text-text-secondary prose-ol:text-text-secondary prose-ul:my-0.5 prose-ol:my-0.5 prose-ul:text-[12px] prose-ol:text-[12px]',
            'prose-li:marker:text-text-quaternary prose-li:my-0',
          ),
        },
        handleKeyDown: (_view, event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            handleSubmit();
            return true;
          }
          return false;
        },
      },
    });

    useImperativeHandle(ref, () => ({
      clear: () => editor?.commands.clearContent(),
      focus: () => editor?.commands.focus(),
      getHTML: () => editor?.getHTML() || '',
    }), [editor]);

    useEffect(() => {
      if (editor && content && content !== editor.getHTML()) {
        editor.commands.setContent(content);
      }
    }, [content, editor]);

    const handleSubmit = useCallback(() => {
      if (!editor || submitting) return;
      const html = editor.getHTML();
      // Check if content is empty (just <p></p> or whitespace)
      const text = editor.getText().trim();
      if (!text) return;
      onSubmit(html);
    }, [editor, onSubmit, submitting]);

    const handleLinkInsert = useCallback(() => {
      if (!editor) return;
      const previousUrl = editor.getAttributes('link').href || '';
      const url = window.prompt('URL', previousUrl);
      if (url === null) return;
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
      } else {
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
      }
    }, [editor]);

    if (!editor) return null;

    const TB = ({ onClick, active, title, children }: { onClick: () => void; active?: boolean; title: string; children: React.ReactNode }) => (
      <button
        type="button"
        onClick={onClick}
        title={title}
        className={clsx(
          'p-1 rounded transition-colors',
          active ? 'bg-accent-violet text-white' : 'text-text-quaternary hover:bg-surface-hover hover:text-text-secondary'
        )}
      >
        {children}
      </button>
    );

    return (
      <div className="border border-border-primary rounded-lg overflow-hidden bg-surface-secondary focus-within:ring-1 focus-within:ring-accent-violet/50">
        <EditorContent editor={editor} />

        {/* Toolbar + submit */}
        <div className="flex items-center justify-between px-2 py-1 border-t border-border-primary/50 bg-surface-tertiary/30">
          <div className="flex items-center gap-0.5">
            <TB onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Grassetto (⌘B)">
              <Bold className="w-3.5 h-3.5" />
            </TB>
            <TB onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Corsivo (⌘I)">
              <Italic className="w-3.5 h-3.5" />
            </TB>
            <TB onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Barrato">
              <Strikethrough className="w-3.5 h-3.5" />
            </TB>
            <TB onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Codice inline">
              <Code className="w-3.5 h-3.5" />
            </TB>
            <div className="w-px h-4 bg-border-primary mx-0.5" />
            <TB onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Lista puntata">
              <List className="w-3.5 h-3.5" />
            </TB>
            <TB onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Lista numerata">
              <ListOrdered className="w-3.5 h-3.5" />
            </TB>
            <TB onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Citazione">
              <Quote className="w-3.5 h-3.5" />
            </TB>
            <TB onClick={handleLinkInsert} active={editor.isActive('link')} title="Link">
              <LinkIcon className="w-3.5 h-3.5" />
            </TB>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[9px] text-text-quaternary hidden sm:block">⌘↵ per inviare</span>
            <button
              onClick={handleSubmit}
              disabled={submitting || !editor.getText().trim()}
              className={clsx(
                'p-1.5 rounded-md transition-colors',
                editor.getText().trim() && !submitting
                  ? 'text-accent-violet hover:bg-accent-violet/10'
                  : 'text-text-quaternary cursor-not-allowed'
              )}
              title="Invia commento"
            >
              {submitIcon || <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        <style>{`
          .is-comment-empty:first-child::before {
            content: attr(data-placeholder);
            float: left;
            color: var(--text-quaternary);
            pointer-events: none;
            height: 0;
            font-size: 12px;
          }
        `}</style>
      </div>
    );
  }
);

CommentEditor.displayName = 'CommentEditor';
